import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Calendar, Clock, MapPin, Users, X, ShieldCheck, Bus, Utensils, UserCheck, PlaneTakeoff } from 'lucide-react';
import { BACKEND_URL } from '../config';
import SEO from '../components/SEO';
import { SitePage, PageHero, SectionHead, CardSkeletons, CtaBand } from '../components/site/ui';
import { BRAND, inr, toNumber, unsplash, waLink } from '../components/site/brand';
import { submitEnquiry } from '../components/site/api';
import { getDestination } from '../components/site/destinations';
import { taxLabel } from '../components/site/taxes';
import '../components/site/package.css';
import './group-tours.css';
import HoneyPot from '../components/site/HoneyPot';

// 'YYYY-MM-DD' is a calendar date: read it in local time so US visitors don't see the previous day
const toDate = (d) => (/^\d{4}-\d{2}-\d{2}$/.test(String(d || '')) ? new Date(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10))) : new Date(d));
const fmtDate = (d) => {
  const date = toDate(d);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const EMPTY = { name: '', email: '', phone: '', travelers: '', message: '' };
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthLabel = (ym) => { const [y, m] = ym.split('-').map(Number); return `${MONTHS[m - 1]} ${y}`; };
const ymOf = (d) => { const x = toDate(d); return Number.isNaN(x.getTime()) ? '' : `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`; };

// Fixed departures (own + partner) from the CRM — selling prices only, base price before taxes.
const toCard = (d) => {
  const dest = getDestination(d.destination);
  return {
    key: `dep-${d.departure_id}`, kind: 'departure', departureId: d.departure_id, title: d.title, destination: d.destination_name || dest?.name || d.destination,
    destinationId: d.destination, date: d.start_date, month: (d.start_date || '').slice(0, 7), nights: d.nights, city: d.departure_city,
    description: d.inclusions, image: dest?.heroImage || '', price: Number(d.price_from) || 0, intl: !!d.international,
    badge: d.guaranteed ? 'Guaranteed departure' : d.few_seats ? 'Few seats left' : '', seatsLabel: d.seats_label, fewSeats: !!d.few_seats, packageId: d.package_id,
  };
};
const tourToCard = (t) => {
  const max = Number(t.max_participants) || 0;
  const left = max ? Math.max(0, max - (Number(t.current_participants) || 0)) : null;
  const dest = getDestination(t.destination);
  return {
    key: `tour-${t.id}`, kind: 'tour', tour: t, title: t.title, destination: t.destination, destinationId: dest?.id || String(t.destination || '').toLowerCase(),
    date: t.departure_date, month: ymOf(t.departure_date), duration: t.duration, description: t.description, image: t.image_url || dest?.heroImage || '',
    price: toNumber(t.price_per_person), intl: dest ? dest.region !== 'India' : null, max, badge: left !== null ? (left > 0 ? `${left} seats left` : 'Waitlist') : '',
  };
};

const GroupToursPage = () => {
  const [tours, setTours] = useState([]);
  const [deps, setDeps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dest, setDest] = useState('');
  const [month, setMonth] = useState('');
  const [selected, setSelected] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [state, setState] = useState({ busy: false, ok: false, err: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // ?demo=1 shows sample departures (no server needed) and never sends enquiries.
  const demo = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') === '1';

  useEffect(() => {
    if (demo) {
      import('../crm/mockDepartures').then((m) => {
        const db = { leads: [], bookings: [], tasks: [], events: [], agents: [] };
        m.seedDepartures(db);
        setDeps(m.publicDepartures(db));
      }).catch(() => setDeps([])).finally(() => setLoading(false));
      return;
    }
    const groupTours = axios.get(`${BACKEND_URL}/api/group-tours`)
      .then((r) => {
        const list = Array.isArray(r.data?.group_tours) ? r.data.group_tours : Array.isArray(r.data) ? r.data : [];
        setTours(list.filter((t) => t.is_active !== false));
      })
      .catch(() => setTours([]));
    const departures = axios.get(`${BACKEND_URL}/api/public/departures`)
      .then((r) => setDeps(Array.isArray(r.data?.departures) ? r.data.departures : []))
      .catch(() => setDeps([]));
    Promise.allSettled([groupTours, departures]).finally(() => setLoading(false));
  }, [demo]);

  const cards = useMemo(() => [...deps.map(toCard), ...tours.map(tourToCard)]
    .sort((a, b) => String(a.date || '9999').localeCompare(String(b.date || '9999'))), [deps, tours]);
  const destOptions = useMemo(() => {
    const m = new Map();
    cards.forEach((c) => { if (c.destinationId && !m.has(c.destinationId)) m.set(c.destinationId, c.destination); });
    return [...m.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [cards]);
  const monthOptions = useMemo(() => [...new Set(cards.map((c) => c.month).filter(Boolean))].sort(), [cards]);
  const shown = cards.filter((c) => (!dest || c.destinationId === dest) && (!month || c.month === month));

  useEffect(() => {
    if (!selected) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [selected]);

  const openEnquiry = (tour) => { setSelected(tour); setState({ busy: false, ok: false, err: '' }); };

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, ok: false, err: '' });
    try {
      if (demo) {
        await new Promise((r) => setTimeout(r, 400)); // demo: pretend to send
      } else if (selected.kind === 'departure') {
        await submitEnquiry({
          name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim(),
          packageId: selected.packageId || '', destination: selected.destination, travelDate: selected.date, travelers: f.travelers,
          message: `Fixed departure: ${selected.title} — ${fmtDate(selected.date)}${selected.city ? ` ex ${selected.city}` : ''} [departure_id: ${selected.departureId}]\n${f.message}`,
          productType: 'group',
        });
      } else {
        const t = selected.tour;
        await submitEnquiry({
          name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim(),
          packageId: t.id, destination: t.destination, travelDate: t.departure_date,
          travelers: f.travelers, message: `Group tour: ${t.title}\n${f.message}`, productType: 'group_tour',
        });
      }
      setState({ busy: false, ok: true, err: '' });
      setF(EMPTY);
    } catch {
      setState({ busy: false, ok: false, err: `Sorry, that didn't go through. Please WhatsApp or call ${BRAND.phone}.` });
    }
  };

  return (
    <SitePage>
      <SEO title="Group Tours · Fixed Departures" description="Join VK Tours fixed-departure group tours with a tour manager, comfortable coaches and Indian meals — or ask us to plan a private group trip." path="/group-tours" />
      <PageHero
        image={unsplash('photo-1529156069898-49953e39b3ac', 2000)}
        imageAlt="Group of friends travelling together"
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Group tours' }]}
        kicker="Fixed departures"
        title="Group"
        accent="tours"
        lede="Travel with like-minded people on scheduled departures — tour manager, transport and meals arranged. Planning for your own family, school or company group? We do that too."
      >
        <a className="vk-btn vk-btn--hot" href="#departures">See departures</a>
        <a className="vk-btn vk-btn--ghost" href={waLink('Hi VK Tours, I want to plan a private group trip.')} target="_blank" rel="noopener noreferrer">Plan a private group</a>
      </PageHero>

      <section className="vk-section">
        <div className="vk-wrap">
          <SectionHead kicker="What’s included" title="Why travel in a group with us" />
          <div className="vk-feats">
            <div className="vk-feat"><UserCheck size={28} /><h3>Tour manager</h3><p>An experienced manager travels with the group.</p></div>
            <div className="vk-feat"><Bus size={28} /><h3>Comfortable coaches</h3><p>Private transport for the whole itinerary.</p></div>
            <div className="vk-feat"><Utensils size={28} /><h3>Indian meals</h3><p>Veg and Jain options arranged where possible.</p></div>
            <div className="vk-feat"><ShieldCheck size={28} /><h3>Better prices</h3><p>Group rates on hotels, sightseeing and flights.</p></div>
          </div>
        </div>
      </section>

      <section className="vk-section vk-section--sand" id="departures" style={{ scrollMarginTop: 'calc(var(--header-h) + 12px)' }}>
        <div className="vk-wrap">
          <SectionHead kicker="Upcoming" title="Group departures" />
          <p className="vk-gt-lede">Join a scheduled group — <strong>even if you are travelling alone</strong>. Prices are per person on twin sharing; single rooms on request.</p>
          {!loading && cards.length > 0 ? (
            <div className="vk-gt-filters" role="search">
              <select className="vk-select" value={dest} onChange={(e) => setDest(e.target.value)} aria-label="Destination">
                <option value="">All destinations</option>
                {destOptions.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <select className="vk-select" value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month">
                <option value="">Any month</option>
                {monthOptions.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
              </select>
              <span className="vk-gt-count" aria-live="polite">{shown.length} departure{shown.length === 1 ? '' : 's'}</span>
            </div>
          ) : null}
          {loading ? <CardSkeletons count={3} /> : shown.length ? (
            <div className="vk-grid vk-grid--3">
              {shown.map((c) => (
                <article key={c.key} className="vk-card vk-gt-card">
                  <div className="vk-card__media">
                    {c.image ? <img src={c.image} alt={c.destination || ''} loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : null}
                    {c.badge ? <span className={`vk-card__off${c.badge.startsWith('Guaranteed') ? ' vk-gt-badge--ok' : ''}`}>{c.badge}</span> : null}
                    {c.date && fmtDate(c.date) ? <span className="vk-gt-date"><b>{toDate(c.date).getDate()}</b>{MONTHS[toDate(c.date).getMonth()].slice(0, 3)}</span> : null}
                  </div>
                  <div className="vk-card__body">
                    <h3 className="vk-card__title">{c.title}</h3>
                    <ul className="vk-gmeta">
                      {c.destination ? <li><MapPin size={15} /> {c.destination}</li> : null}
                      {c.date && fmtDate(c.date) ? <li><Calendar size={15} /> {fmtDate(c.date)}</li> : null}
                      {c.nights ? <li><Clock size={15} /> {c.nights} nights / {c.nights + 1} days</li> : c.duration ? <li><Clock size={15} /> {c.duration}</li> : null}
                      {c.city ? <li><PlaneTakeoff size={15} /> From {c.city}</li> : null}
                      {c.max ? <li><Users size={15} /> Max {c.max} travellers</li> : null}
                    </ul>
                    {c.description ? <p className="vk-gdesc">{c.description}</p> : null}
                    <div className="vk-card__price">
                      <div>
                        {c.price ? <small>from</small> : null}
                        <strong>{c.price ? inr(c.price) : 'Price on request'}</strong>
                        {c.price ? <small>per person {c.intl === null ? '+ taxes' : taxLabel(c.intl)}</small> : null}
                      </div>
                      <button type="button" className="vk-btn vk-btn--hot vk-btn--sm" onClick={() => openEnquiry(c)}>{c.kind === 'departure' ? 'Book / Enquire' : 'Enquire'}</button>
                    </div>
                    {c.seatsLabel ? <p className={`vk-gt-seats${c.fewSeats ? ' is-few' : ''}`}>{c.seatsLabel}</p> : null}
                  </div>
                </article>
              ))}
            </div>
          ) : cards.length ? (
            <div className="vk-empty">
              <h3>No departures for this choice</h3>
              <p>Try another month or destination — or ask us and we’ll add you to the next group.</p>
              <button type="button" className="vk-btn vk-btn--hot" onClick={() => { setDest(''); setMonth(''); }}>Show all departures</button>
            </div>
          ) : (
            <div className="vk-empty">
              <h3>New departures coming soon</h3>
              <p>Tell us your group size and destination — we’ll plan a private group trip or add you to the next departure.</p>
              <a className="vk-btn vk-btn--hot" href={waLink('Hi VK Tours, please share upcoming group tour departures.')} target="_blank" rel="noopener noreferrer">Ask on WhatsApp</a>
            </div>
          )}
        </div>
      </section>

      <CtaBand title="Travelling as a group?" accent="We'll plan it." message="Hi VK Tours, I want a quote for a group trip." />

      {selected ? (
        <div className="vk-modal" role="dialog" aria-modal="true" aria-labelledby="g-title" onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div className="vk-modal__box">
            <button type="button" className="vk-modal__x" aria-label="Close" onClick={() => setSelected(null)}><X size={20} /></button>
            <p className="vk-kicker">{selected.kind === 'departure' ? 'Book a group departure' : 'Group tour enquiry'}</p>
            <h2 id="g-title" className="vk-modal__title">{selected.title}</h2>
            {selected.kind === 'departure' ? (
              <p className="vk-gt-modalsub">{fmtDate(selected.date)}{selected.nights ? ` · ${selected.nights} nights` : ''}{selected.city ? ` · from ${selected.city}` : ''}{selected.price ? ` · from ${inr(selected.price)} per person ${taxLabel(selected.intl)}` : ''}</p>
            ) : null}
            {state.ok ? (
              <div className="vk-alert vk-alert--ok" role="status">{selected.kind === 'departure' ? 'Thank you — our team will confirm seat availability and call you shortly.' : 'Thank you — we’ll contact you within 24 hours.'}</div>
            ) : (
              <form className="vk-form" onSubmit={submit}><HoneyPot />
                <div className="vk-field"><label htmlFor="g-name">Full name *</label><input id="g-name" required value={f.name} onChange={set('name')} autoComplete="name" /></div>
                <div className="vk-form__row">
                  <div className="vk-field"><label htmlFor="g-phone">Phone *</label><input id="g-phone" required type="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" /></div>
                  <div className="vk-field"><label htmlFor="g-trav">Travellers *</label><input id="g-trav" required type="number" min="1" max="200" value={f.travelers} onChange={set('travelers')} /></div>
                </div>
                <div className="vk-field"><label htmlFor="g-email">Email *</label><input id="g-email" required type="email" value={f.email} onChange={set('email')} autoComplete="email" /></div>
                <div className="vk-field"><label htmlFor="g-msg">Message</label><textarea id="g-msg" rows={3} value={f.message} onChange={set('message')} /></div>
                {state.err ? <div className="vk-alert vk-alert--err" role="alert">{state.err}</div> : null}
                <button type="submit" className="vk-btn vk-btn--hot vk-btn--block" disabled={state.busy}>{state.busy ? 'Sending…' : 'Send enquiry'}</button>
              </form>
            )}
          </div>
        </div>
      ) : null}
    </SitePage>
  );
};

export default GroupToursPage;
